import { type ImgHTMLAttributes, useState } from 'react';

/**
 * An <img> that says when it has loaded (`data-loaded="true"`). Screen audits and e2e use it to wait for a picture before they
 * take a screenshot: without it a capture races the image, and a golden can record the empty block while every other run sees
 * the picture. It changes nothing visually.
 *
 * `load` fires when the bytes have arrived, and the picture is painted a frame or two later when it still has to be decoded (the
 * big full-bleed screen art uses `decoding="async"`). A capture right after `load` could still see the empty block, so the flag
 * waits for `decode()` where the browser has it (a failed decode counts as done: the audit then shows whatever is there).
 */
export function LoadedImage(props: ImgHTMLAttributes<HTMLImageElement>) {
  const [loaded, setLoaded] = useState(false);
  return (
    // biome-ignore lint/a11y/useAltText: the caller passes `alt` through props
    <img
      {...props}
      data-loaded={loaded ? 'true' : 'false'}
      onLoad={(e) => {
        const img = e.currentTarget;
        if (typeof img.decode === 'function') img.decode().then(() => setLoaded(true), () => setLoaded(true));
        else setLoaded(true);
        props.onLoad?.(e);
      }}
    />
  );
}
